{
  "activities": [
    {
      "title": "The Cross-Platform Synthesiser (Chat + Connected Apps)",
      "tool": "atlassian",
      "preferences": {
        "product-analyst-delivery": ["beginner"],
        "product-analyst-product": ["beginner"],
        "product-analyst-ux": ["beginner"]
      },
      "content": {
        "paragraphs": [
          "Objective: Use Rovo Chat to generate a comprehensive project summary by pulling data across Jira, Confluence, and connected third-party apps (like Google Drive or Slack).",
          "Scenario: You are taking over a project or preparing for a big sync. The data you need isn't just in Jira, there might be a project brief in Google Drive, meeting notes in Confluence, and chatter in Slack. Instead of hunting through tabs, you are going to let Rovo Chat read it all and give you the highlights."
        ],
        "steps": [
          "Open up a current or old Jira or Confluence board and click the Rovo Chat icon",
          "Prompt the AI to summarise a specific, active project by explicitly asking it to look across your tools. Type something like: \"Summarise the current status of [Project Name]. Please pull context from [Jira, confluence, google docs and slack].\"",
          "Review the response. Ask a follow-up question to format it for sharing: \"Great, now turn this into a short, 3-bullet executive summary that I can paste into Slack.\""
        ],
        "link": "https://www.atlassian.com/try/cloud/signup",
        "estimatedTime": "10 minutes",
        "verification": "You succeeded if Rovo’s response successfully blended information from an external connected app (which you can verify by checking the citation links [1] in its response) and reformatted it based on your second prompt."
      },
      "id": "cross-platform-synthesiser"
    },
    {
      "title": "The First Draft (Out-of-the-Box Rovo Agents)",
      "tool": "atlassian",
      "preferences": {
        "product-analyst-delivery": ["intermediate"],
        "product-analyst-product": ["intermediate"],
        "product-analyst-ux": ["intermediate"]
      },
      "content": {
        "paragraphs": [
          "Objective: Find, activate and collaborate with a pre-built Rovo Agent to generate documentation from existing Jira data.",
          "Scenario: You need to draft a set of Release Notes (or a UX Design QA checklist) for a feature that is about to launch. All the details are scattered across a few messy Jira tickets and comments. Instead of reading them all and typing it from scratch, you are going to let a specialised Rovo Agent write the first draft for you."
        ],
        "steps": [
          "Look for the Agents tab in your top Atlassian navigation bar (or open the Rovo Chat panel and click the Agents icon).",
          "Browse the directory for an out-of-the-box agent provided by Atlassian. Look for one like Release Notes Writer, Content Drafter, or Task Break-down. Click on it to open a chat specifically with that Agent.",
          "Introduce the task to the Agent. Type a prompt like: \"I need to write a summary document for our upcoming launch. Please base it ONLY on the following tickets:\"",
          "Use the @ symbol in the chat to search for and tag 2 or 3 real Jira tickets (or an Epic) related to a project you are working on. Hit send.",
          "Review the draft the Agent creates. If you want to tweak it, ask it to adjust the tone (e.g., \"Format this as a checklist\")."
        ],
        "link": "https://www.atlassian.com/try/cloud/signup",
        "estimatedTime": "15 minutes",
        "verification": "Go to your Jira board and create a brand new test ticket, Intentionally leave the \"Assignee\" field completely blank and save the ticket, refresh your page, if your automation worked, your face (or the creator's face) should automatically appear as the Assignee!"
      },
      "id": "first-draft"
    },
    {
      "title": "The Personal Backlog Manager in Jira",
      "tool": "atlassian",
      "preferences": {
        "product-analyst-delivery": ["beginner"],
        "product-analyst-product": ["beginner"],
        "product-analyst-ux": ["beginner"]
      },
      "content": {
        "paragraphs": [
          "Objective: Use Rovo Chat’s native Jira skills to instantly retrieve, prioritise, and format your assigned work items without building manual JQL filters.",
          "Scenario: It’s Monday morning. You have tickets scattered across different epics and maybe even different Jira boards. You need to know exactly what is on your plate this week and what needs your attention first."
        ],
        "steps": [
          "Open up a current or old Jira board and open a fresh Rovo Chat window.",
          "Instead of navigating to your standard \"Assigned to me\" dashboard, give Rovo Chat a direct command. Type: \"Summarise the status of all open Jira tickets assigned to me. Include direct ticket links and order them by priority and deadline.\"",
          "Review and take action on that data. Give Rovo a final command to prep your morning stand-up update: \"Draft a quick status update based on this list, highlighting my two highest priority tickets and summarising any new comments left on them.\""
        ],
        "link": "https://www.atlassian.com/try/cloud/signup",
        "estimatedTime": "10 minutes",
        "verification": "You successfully completed this if Rovo Chat instantly pulled your open tickets, correctly identified the highest priorities, and drafted a cohesive text update that you could immediately read at your daily stand-up."
      },
      "id": "personal-backlog-manager"
    },
    {
      "title": "The Safety Net (Simple Automation)",
      "tool": "atlassian",
      "preferences": {
        "product-analyst-delivery": ["intermediate"],
        "product-analyst-product": ["intermediate"],
        "product-analyst-ux": ["intermediate"]
      },
      "content": {
        "paragraphs": [
          "Objective: Build a basic if-then Jira Automation flow using the core building blocks: triggers, conditions, and actions.",
          "Scenario: Tickets are falling through the cracks on your board. People are quickly logging bugs or design tweaks, but they are forgetting to assign them to anyone. You are going to build a \"safety net\" automation: if a new ticket is created and the assignee is left blank, then Jira will automatically assign it to the person who created the ticket so it doesn't get lost."
        ],
        "steps": [
          "Navigate to an active or previous Jira project where you have admin/automation permissions. Go to Project Settings (usually at the bottom of the left sidebar) > Automation.",
          "Click the Create rule/Create Flow button in the top right corner and select \"create from scratch\"",
          "(The Trigger): Select Issue created from the list of triggers, then click Save.  \n(The Condition): Click New condition/work items field condition  \n> Issue fields condition. Set the Field to Assignee, the Condition to is empty, and click Save.  \n(The Action): Click New action > Assign issue/assign work item. In the dropdown, select User who triggered the event (or you can pick your own name). Click Save.",
          "Give your rule a name (e.g., \"Auto-Assign Empty Tickets\") and click Turn it on."
        ],
        "link": "https://www.atlassian.com/try/cloud/signup",
        "estimatedTime": "15 minutes",
        "verification": "You successfully completed this if the specialised Agent output a structured, professional draft directly in the chat that accurately summarised the data from the specific Jira tickets you tagged, effectively saving you 30 minutes of manual typing."
      },
      "id": "safety-net"
    },
    {
      "title": "The Specialist (Custom Rovo Agent)",
      "tool": "atlassian",
      "preferences": {
        "product-analyst-delivery": ["advanced"],
        "product-analyst-product": ["advanced"],
        "product-analyst-ux": ["advanced"]
      },
      "content": {
        "paragraphs": [
          "Objective: Use Rovo Studio to build, instruct, and test a custom, purpose-built Rovo Agent.Scenario: You want an AI assistant dedicated purely to the history of a specific project. It needs to be highly organised, only reference approved documentation, and speak with a highly formal tone. You are going to build this agent from scratch."
        ],
        "steps": [
          "Click on the Agents tab in your top Atlassian navigation bar (or open the Rovo Chat panel and click the Agents icon) and select Create an Agent to open Rovo Studio.",
          "Give your agent a name (e.g., \"The Project Historian\"), write a short description, and pick a custom avatar.",
          "(Knowledge): An agent is only as smart as its data. Under the Knowledge section, click Add Knowledge and link the agent directly to the Jira board or Confluence space of a current or old project.  \n(Instructions): This is where you dictate behaviour. In the prompt box, write strict guardrails: \"You are the Project Historian. You must only answer questions based on the knowledge sources provided. If you do not know the answer, you must say 'I am afraid I do not have a record of that, sir/madam.' Maintain the formal, polite tone of a traditional British butler at all times.\" [2]  \n(Testing): Look at the right side of the screen; there is a live preview chat. Test your agent's boundaries before publishing. Ask a completely off-topic question, such as, \"Write a poem about a dog.\"",
          "Click Publish to make the agent available to yourself (or your team)."
        ],
        "link": "https://www.atlassian.com/try/cloud/signup",
        "estimatedTime": "30 minutes",
        "verification": "You have succeeded if, during testing, the agent politely refuses to write the poem and stays strictly in character, proving that your behavioural instructions are working correctly."
      },
      "id": "specialist"
    },
    {
      "title": "Natural Language SQL & Data Exploration",
      "tool": "coco",
      "preferences": {
        "data": ["beginner"]
      },
      "content": {
        "paragraphs": [
          "Objective: Use CoCo (Snowflake Copilot) in Snowsight to generate analytical SQL queries, inspect data distributions, and aggregate customer metrics using natural language prompts against built-in Snowflake sample data.",
          "Scenario: You need to analyse customer purchasing trends across market segments. Instead of writing complex SQL joins and aggregations manually, you will use CoCo to interrogate Snowflake's standard sample database directly inside your worksheet."
        ],
        "steps": [
          "Go into Snowflake and use:  \n**Role**: COCO_AUGMENT_ENABLEMENT  \n**Warehouse**: COCO_AUGMENT_WH  \n**Database / schema**: COCO_AUGMENT_DB.ENABLEMENT",
          "Open a new SQL Worksheet in Snowsight and set your database context to SNOWFLAKE_SAMPLE_DATA.TPCH_SF1.",
          "Open the CoCo side panel and enter your initial prompt: \"Write a query to calculate total account balance and customer count grouped by market segment using the CUSTOMER table.\"",
          "Review the generated SQL, copy the code, and execute the query in your worksheet.",
          "Ask CoCo a follow-up prompt: \"Modify this query to join the ORDERS table and show the top 5 market segments by total revenue in 1995.\"",
          "Run the updated query and toggle the results view to Chart to visually verify segment performance."
        ],
        "link": "https://app.snowflake.com/",
        "estimatedTime": "10 minutes",
        "verification": "You succeeded if CoCo generates valid Snowflake SQL joining CUSTOMER and ORDERS from SNOWFLAKE_SAMPLE_DATA.TPCH_SF1 and renders the top 5 revenue-generating market segments without syntax errors."
      },
      "id": "natural-language-sql"
    },
    {
      "title": "Query Optimisation & Business Logic Encoding",
      "tool": "coco",
      "preferences": {
        "data": ["beginner"]
      },
      "content": {
        "paragraphs": [
          "Objective: Use CoCo to refactor raw SQL queries for execution efficiency and encode custom financial logic using native Snowflake sample tables.",
          "Scenario: Upstream reporting queries on line items run slowly and lack standardized calculations for order profitability. You will use CoCo to optimise the query execution structure and apply custom margin calculations."
        ],
        "steps": [
          "In your Snowsight worksheet, ask CoCo to generate a simple multi-join query using SNOWFLAKE_SAMPLE_DATA.TPCH_SF1.LINEITEM as the starting table, joining to relevant TPCH tables such as ORDERS, CUSTOMER, SUPPLIER, and PART. Suggested prompt: “Generate a basic multi-join SQL query for Snowflake using SNOWFLAKE_SAMPLE_DATA.TPCH_SF1.LINEITEM as the base table. Join it to ORDERS, CUSTOMER, SUPPLIER, and PART, and return a useful set of order, customer, supplier, product, quantity, price, discount, and return-flag fields. Keep the query deliberately straightforward rather than heavily optimized, as I will optimize it in the next step.” Paste/keep the generated query in the worksheet.",
          "Paste a basic multi-join query selecting from SNOWFLAKE_SAMPLE_DATA.TPCH_SF1.LINEITEM into your Snowsight worksheet.",
          "Highlight the query code block, open CoCo, and prompt: \"Optimise this query for Snowflake execution efficiency by simplifying aggregations and eliminating redundant joins.\"",
          "Add custom logic by prompting CoCo: \"Add a calculated column for 'Net Profit Margin' using the formula: (L_EXTENDEDPRICE \\* (1 - L_DISCOUNT)) - L_SUPPCOST, grouped by return flag.\"",
          "Review the refactored SQL generated by CoCo and click keep all into Worksheet.",
          "Execute the optimised script and inspect the calculated margin metrics."
        ],
        "link": "https://app.snowflake.com/",
        "estimatedTime": "10 minutes",
        "verification": "You succeeded if CoCo refactors your SQL, accurately implements the custom mathematical formula against SNOWFLAKE_SAMPLE_DATA.TPCH_SF1.LINEITEM, and returns the summarized margin metrics cleanly in your results panel."
      },
      "id": "query-optimisation"
    },
    {
      "title": "Create and edit components, styles and variables",
      "tool": "figma-ai",
      "preferences": {
        "product-analyst-ux": ["intermediate"]
      },
      "content": {
        "paragraphs": [
          "Objective: Learn how to use Figma AI to extract values and create variables."
        ],
        "steps": [
          "Open the link to the Figma Agent Playground and select ‘open in Figma’ or if you have already opened this playground return back to it.",
          "Read through the page ‘Introduction to agents in Figma’ to get a basic understanding",
          "Select page ‘Automate repetitive tasks’ and find ‘Systemisation’",
          "Find the ‘Try it out’ section and give it a go!"
        ],
        "link": "https://www.figma.com/community/file/1636475245178613559/figma-agent-playground",
        "estimatedTime": "15 minutes",
        "verification": "You have succeeded if you have extracted all the type size values on the screen and created a number variables collection out of it."
      },
      "id": "create-and-edit-components-styles-and-variables"
    },
    {
      "title": "Design to code with Figma Make",
      "tool": "figma-ai",
      "preferences": {
        "product-analyst-ux": ["intermediate"]
      },
      "content": {
        "paragraphs": [
          "Objective: Describe the experience you want to build and create a prototype using Figma make."
        ],
        "steps": [
          "Open the link to the Design to code tool.",
          "Describe the experience you want to build within the text box.",
          "If you can’t think of a prototype you’d like to create, use one of the pre-made ideas displayed",
          "Press the blue arrow and try it out!"
        ],
        "link": "https://www.figma.com/solutions/design-to-code/",
        "estimatedTime": "15 minutes",
        "verification": "You have succeeded if you have a prototype based on the prompt you shared."
      },
      "id": "design-to-code-with-figma-make"
    },
    {
      "title": "Populate realistic text fast",
      "tool": "figma-ai",
      "preferences": {
        "product-analyst-ux": ["beginner"]
      },
      "content": {
        "paragraphs": [
          "Objective: Move from manually typing filler text across repeated components. Learn how to use Figma AI to instantly populate realistic, context-specific text."
        ],
        "steps": [
          "Open the link to the Figma Agent Playground and select ‘open in Figma’ or if you have already opened this playground return back to it.",
          "Read through the page ‘Introduction to agents in Figma’ to get a basic understanding",
          "Select page ‘Automate repetitive tasks’ and find ‘DS documentation’",
          "Find the ‘try it out’ which is titled ‘Generate design system documentation’",
          "Select the component that lists ‘Menu items’",
          "Select agents using the menu on the left and prompt to change the text for example ‘Change the text to fast food items’"
        ],
        "link": "https://www.figma.com/community/file/1636475245178613559/figma-agent-playground",
        "estimatedTime": "10 minutes",
        "verification": "You have succeeded if the text has changed based on your prompt."
      },
      "id": "populate-realistic-text-fast"
    },
    {
      "title": "The Instant UX Writer (Rewrite, Shorten, & Translate)",
      "tool": "figma-ai",
      "preferences": {
        "product-analyst-ux": ["beginner"]
      },
      "content": {
        "paragraphs": [
          "Objective: Learn how to use Figma AI to instantly translate text."
        ],
        "steps": [
          "Open the link to the Figma Agent Playground and select ‘open in Figma’ or if you have already opened this playground return back to it.",
          "Read through the page ‘Introduction to agents in Figma’ to get a basic understanding",
          "Select page ‘Automate repetitive tasks’ and find ‘DS documentation’",
          "On the ‘Component documentation w/ reference’ frame select the footer which include text like ‘Use cases’ ‘Explore’ ‘Resources’ etc",
          "Select agents using the menu on the left and prompt Figma to translate the text for example ‘Change the text to German’"
        ],
        "link": "https://www.figma.com/community/file/1636475245178613559/figma-agent-playground",
        "estimatedTime": "10 minutes",
        "verification": "You have succeeded if the text has changed based on your prompt and you have been able to translate text without leaving Figma."
      },
      "id": "instant-ux-writer"
    },
    {
      "title": "Using agents with design systems",
      "tool": "figma-ai",
      "preferences": {
        "product-analyst-ux": ["advanced"]
      },
      "content": {
        "paragraphs": [
          "Objective: Use your selected libraries assets to edit or create designs with Figma agent."
        ],
        "steps": [
          "Open the link to the Figma Agent Playground and select ‘open in Figma’ or if you have already opened this playground return back to it.",
          "Read through the page ‘Introduction to agents in Figma’ to get a basic understanding",
          "Select page ‘Using agents with design systems’",
          "Explore the page and find a ‘try it out’ which is titled ‘Generate an edit using your design system’",
          "Follow the instructions on the frame."
        ],
        "link": "https://www.figma.com/community/file/1636475245178613559/figma-agent-playground",
        "estimatedTime": "20 minutes",
        "verification": "You have succeeded if you have generated an edit using your design system."
      },
      "id": "using-agents-with-design-systems"
    },
    {
      "title": "A simple interactive form",
      "tool": "gemini",
      "preferences": {
        "product-developers-engineers": ["beginner"]
      },
      "content": {
        "paragraphs": [
          "Objective: You want a working prototype of a signup form for a feature you're speccing out, without writing any code yourself."
        ],
        "steps": [
          "Go to gemini click Canvas on the drop down option from the prompt bar, and ask it to build HTML signup form with name, email, and a \"notify me\" toggle.",
          "Use the live preview to test it, and ensure gemini has added basic validation (eg. reject empty email)",
          "Ask Gemini to animate the toggle button and test again"
        ],
        "link": "https://gemini.google.com/app",
        "estimatedTime": "10 minutes",
        "verification": "You have succeeded if you have built a working prototype without writing any code yourself."
      },
      "id": "simple-interactive-form"
    },
    {
      "title": "A small interactive app",
      "tool": "gemini",
      "preferences": {
        "product-developers-engineers": ["intermediate"]
      },
      "content": {
        "paragraphs": [
          "Objective: Your team wants a quick internal tool, a tip calculator, to demo an idea before committing engineering time."
        ],
        "steps": [
          "Go to gemini click Canvas on the drop down option from the prompt bar, prompt for a simple React-based calculator app (or another front end language you're more proficient with and can review) with at least one interactive input (slider or dropdown). Ensure the calculator is useful for basic mathematical tasks: addition, subtraction, multiplication and division and isn’t a complex scientific calculator. Ask for a modular design rather than a one file app.",
          "Use the console to find existing errors if any were introduced,  and ask Gemini to fix it using the logs.",
          "Iterate on styling by editing specific code sections directly rather than regenerating the whole app."
        ],
        "link": "https://gemini.google.com/app",
        "estimatedTime": "10 minutes",
        "verification": "You successfully built a quick internal tool to demo an idea before committing engineering time."
      },
      "id": "small-interactive-app"
    },
    {
      "title": "Apps Script data sorting and building custom menus",
      "tool": "gemini",
      "preferences": {
        "data": ["advanced"],
        "enabling-teams": ["advanced"],
        "product-analyst-delivery": ["advanced"],
        "product-analyst-product": ["advanced"],
        "product-analyst-ux": ["advanced"]
      },
      "content": {
        "paragraphs": [
          "Objective: Automate the tedious task of organising your data and running analysis with a custom menu."
        ],
        "steps": [
          "Create a copy of the google sheet which contains some sample data using the button 'Open in Gemini and start' at the bottom.",
          "Ask Gemini in the sheet: ‘Write an Apps Script that looks at the master sheet. For every unique value in the region column, create a new tab with that region’s name and copy the related rows into those tabs as a copy. Ensure the headers are copied into the new tabs. At the same time highlight the rows by status and sort by priority. Generate a system instruction tab from scratch too. Generate a custom menu to re-apply the sorting criteria when we need to. Provide some further brainstorming and think of edge cases we could encounter in future runs.’",
          "Iterate and ask Gemini to think of edge cases and future proofing on the apps script code. Sometimes Gemini within sheets (as it is currently in Beta) will ask a series of qualifying further options too. Incorporate a selection of suggestions to test with.",
          "Select **Extensions > Apps Script**. In the app script container (the light grey box) paste your script over the existing text (‘function myFunction() { }’). Then click the **floppy disk icon** to save the script (the script is usually called: code.gs).",
          "Exit the Apps Script window and navigate back to your sheet. Refresh the sheet.",
          "Shortly after refreshing, at the end of the menu options at the top of the sheet (next to the ‘help’ or ‘accessibility’ options), you should now see a custom menu called **Custom Actions**. Open the drop-down of this new custom menu. You should see two drop-down options to split and sort the data - select each one in turn. You may be prompted to authorise this; if so, make sure you complete all steps in the pop-up authorisation window.",
          "Now imagine that you want to share the data in an engaging way with each hub - and enable them to update it directly. Ask Gemini to **create an interactive app in a new canvas**. Use precision stack prompting to be clear on the purpose/context etc. Keep iterating until it is visualised the way you like.",
          "Share this tab as a standalone web page. In the top menu, click **File > Share > Publish to web**. Under the Link tab, click the dropdown that currently says **Entire Document** and select the name of the **tab** you want to share instead. Ensure the second dropdown is set to **Web page**. Click the **Publish** button and click **OK** to confirm. Copy the link it generates; this could now be pasted into a Google site for example.",
          "Now you have practised with dummy data, try this out on a spreadsheet of your own…!"
        ],
        "link": "https://docs.google.com/spreadsheets/d/14qSEcKiIuz0yKoffhQIL5FzfYZ6enOsQwRR5Zj6lvzk/copy",
        "estimatedTime": "25 minutes",
        "verification": "You have been successful when refreshing your Google Sheet reveals the \"**Custom Actions**\" menu, running its options automatically creates region-specific tabs with sorted, highlighted data, and publishing your chosen tab via \"**Publish to web**\" generates a working shareable link."
      },
      "id": "apps-script-data-sorting"
    },
    {
      "title": "Automate an analytical task with a custom Skill",
      "tool": "gemini",
      "preferences": {
        "data": ["intermediate"],
        "enabling-teams": ["intermediate"],
        "product-analyst-delivery": ["intermediate"],
        "product-analyst-product": ["intermediate"],
        "product-analyst-ux": ["intermediate"]
      },
      "content": {
        "paragraphs": [
          "Objective: Create a reusable Custom Skill in Google Workspace Studio to automate a repetitive analytical task across your files."
        ],
        "steps": [
          "Choose a recurring analytical task you perform on Google files (e.g., Slides, Docs, Sheets), such as writing executive summaries, tracking action items or checking tone and brand alignment.",
          "Go to **Google Workspace Studio\\*** (studio.workspace.google.com). Click the large blue **+ Add tool** button. Select **Skill: Create custom AI instructions**. Name your skill at the top (e.g. Executive Summary Generator).  *[\\*Note: once you have access to Gemini Enterprise, you can also create skills directly there. As of 14.09.26, Skills created in Google Workspace Studio do not transfer to Gemini Enterprise.]*",
          "In the Customise your skill box on the left, write your prompt instructions. You can use the built-in AI assistant to draft them.  \nExample: \"Role: You are an executive chief of staff. Analyse the provided file and write a concise, high-impact executive summary for senior leadership. Instructions:  \nIn 2–3 sentences, explain the overarching goal or proposition of this deck/file.  \nExtract the key critical metrics, achievements, or strategic points using bullet points.  \nIdentify any explicitly stated risks, budget needs or timeline dependencies.  \nFormatting rules:  \nUse clear bold headings.  \nKeep total length under 250 words.  \nUse direct, executive-level language (no fluff or introductory filler).”",
          "Click the **Turn on** (or Save) button in the top right corner to activate your skill across your Workspace apps.",
          "Check out the other **templates from Google** in the **Skills** section of Workplace Studio (studio.workspace.google.com/skills)."
        ],
        "link": "http://studio.workspace.google.com/",
        "estimatedTime": "15 minutes",
        "verification": "Open a live file (e.g. Google Slide deck) in a new tab. Click the Gemini icon in the top right toolbar to open the side panel. Type @ (or /) in the chat box to view your saved custom skills. Select your newly created skill (e.g., @Executive Summary Generator) and press Enter. Review the output in the panel. If it missed key details or was too wordy, go back to Workspace Studio, tweak your prompt instructions, and re-test."
      },
      "id": "analytical-custom-skill"
    },
    {
      "title": "Build a custom personal utility app",
      "tool": "gemini",
      "preferences": {
        "data": ["beginner"],
        "enabling-teams": ["beginner"],
        "product-analyst-delivery": ["beginner"],
        "product-analyst-product": ["beginner"],
        "product-analyst-ux": ["beginner"]
      },
      "content": {
        "paragraphs": [
          "Objective: Create a simple web tool that would make your day easier (e.g. a random decision-maker, a simple checklist, a unit converter, an Eisenhower matrix)."
        ],
        "steps": [
          "Open Gemini\\* (gemini.google.com/app), click the **+ button**, and select **Canvas**. [\\*Note, you will also be able to open Canvas from Gemini Enterprise; click **New Chat**, select the **Tools** icon within the bottom of the chat box, then select **Canvas**.]",
          "Write a prompt using the precision stack prompt framework (i.e. set the context, constraints, examples and output specification) to describe exactly how you want your app to work and what features it should have.  \nExample: ‘Build a Pomodoro timer web app to manage my focus time. It must allow custom work/break intervals, display a visual countdown and play an audio chime when time expires. Include play/pause/reset buttons. Include pre-set buttons for standard intervals like '25 min Work' and '5 min Break'. Feature a clean, modern layout with easy-to-read text.’",
          "Once Canvas builds the app, practice refining requirements by sending follow-up prompts (e.g., ‘Change the primary accent color to purple and make the start/pause buttons 20% larger’).",
          "You can download the app and save it for future use."
        ],
        "link": "https://gemini.google.com/app",
        "estimatedTime": "10 minutes",
        "verification": "Open the downloaded file in your web browser. Interact with the app (e.g., click 'Start' on your timer) to ensure the core functions work and check that your design tweaks are visible. If anything is broken or missing, describe the bug to Gemini and ask it to fix the code!"
      },
      "id": "custom-utility-app"
    },
    {
      "title": "Schedule an automated custom news bulletin",
      "tool": "gemini",
      "preferences": {
        "data": ["beginner"],
        "enabling-teams": ["beginner"],
        "product-analyst-delivery": ["beginner"],
        "product-analyst-product": ["beginner"],
        "product-analyst-ux": ["beginner"]
      },
      "content": {
        "paragraphs": [
          "Objective: Use Gemini to get news briefs daily, weekly or monthly on a topic of your choice."
        ],
        "steps": [
          "Identify an emerging industry trend, or something you want to learn more about on a regular basis.",
          "Open **Gemini\\*** (gemini.google.com/app), click the **+ button**, and select **News digest**. *[\\*Note: you can also schedule actions in* ***Gemini Enterprise****; in the side panel, under* ***Agents****, select* ***New Agent > Chat Agent*** *and enter your prompt. You can click* ***Schedule*** *to determine your desired date and time.]*",
          "Add your title and then your instructions using a precision stack prompt framework (i.e. setting the context, constraints, examples and output specification).  \nExample:  \n''Act as an AI Research Analyst supporting AND Digital Client Partnerships. Search for 3 critical enterprise AI research updates, market insights, or platform developments published in the last 7 days.  \nFocus on market signals that inform client strategy and situational awareness:  \n- Enterprise AI Dynamics: Budget movements, operational friction, adoption barriers, ROI metrics, governance, or organizational readiness.  \n- Platform & Ecosystem Updates: Enterprise-relevant updates from core AI providers (e.g., Microsoft, AWS, Google, OpenAI, Anthropic).  \nConstraints: strictly published within the last 7 days. Max 3 items total. Under 2 minutes total read time. Use an objective, clear tone, focused on operational reality.  \nProvide 3 numbered items using this exact structure:  \n\\* Market Insight: 1–2 sentence summary of the research or announcement.  \n\\* What to Listen Out For: 1–2 sentences on the specific themes, challenges, or questions clients might raise regarding this development.  \n\\* Source: Link to the source.'",
          "Select how often you want to receive this and at what time.",
          "Navigate to **Settings > Scheduled** actions in your **Gemini** sidebar to confirm your new bulletin is listed and toggled '**On'**."
        ],
        "link": "https://gemini.google.com/app",
        "estimatedTime": "15 minutes",
        "verification": "Check your Gemini app or notifications around your chosen delivery time to read your first official digest! If the content isn't quite hitting the mark over the next few days, edit and refine your prompt instructions."
      },
      "id": "automated-news-bulletin"
    },
    {
      "title": "The API & Mock Data Generator",
      "tool": "gemini",
      "preferences": {
        "product-developers-engineers": ["beginner"]
      },
      "content": {
        "paragraphs": [
          "Objective: Use Gemini to rapidly generate realistic JSON payloads, edge-case test data, and unit tests from plain-English requirements.",
          "Scenario: You are building a new feature or API endpoint. Before writing the backend implementation, you need to generate realistic mock data and write unit tests covering edge cases (such as missing fields or invalid inputs) without typing out manual dummy objects for 20 minutes."
        ],
        "steps": [
          "Think of a feature or backend endpoint you are currently working on (e.g., a User Registration API, a Payment Webhook, or a Search Query endpoint).",
          "Navigate to Gemini at gemini.google.com and write a prompt describing the required schema and edge cases.  \n(Example: \"Generate a JSON mock payload for a User Profile endpoint. Include standard fields like user_id, email, and created_at, but also include edge cases like null optional fields, malformed email strings, and special characters in the name field. Then, write unit tests in [Python/JavaScript/Java] to validate this schema.\")",
          "Review the generated code and JSON output.",
          "Use a follow-up prompt to refine the code (e.g., \"Refactor these unit tests to use [PyTest / Jest / JUnit] and add inline documentation comments for each test case.\").",
          "If you’ve been using an applicable example. Copy the generated test suite and mock data directly into your local development environment or IDE."
        ],
        "link": "https://gemini.google.com/app",
        "estimatedTime": "15 minutes",
        "verification": "You successfully completed this if Gemini generated syntactically valid JSON payloads along with a runnable unit test suite tailored to your language and framework, saving you from manually constructing test data. Otherwise, attempt to reiterate on the solution to fix any bugs with Gemini."
      },
      "id": "api-and-mock-data-generator"
    },
    {
      "title": "The Security & Code Review Auditor (Custom Gem)",
      "tool": "gemini",
      "preferences": {
        "product-developers-engineers": ["intermediate"]
      },
      "content": {
        "paragraphs": [
          "Objective: Create a custom AI Gem that acts as a Principal Engineer and Security Auditor to review code snippets, PR descriptions, and architectural choices before you submit a Pull Request.",
          "Scenario: You want to catch bugs, performance bottlenecks, and security vulnerabilities before your peers review your Pull Request. You will build a dedicated AI reviewer tuned to your team's coding standards and security guidelines."
        ],
        "steps": [
          "Navigate to Gemini at gemini.google.com and open Gem Manager (bottom left settings button), then click New Gem.",
          "Name your Gem (e.g., \"Senior PR & Security Auditor\") and define its core role: \"Act as a Principal Software Engineer and Application Security Specialist.\"",
          "Hard-code 3 mandatory evaluation lenses into its instructions:  \nSecurity: OWASP Top 10 vulnerabilities, input sanitisation, and credential handling.  \nPerformance: Time/space complexity, memory leaks, and unnecessary database/API calls.  \nMaintainability: Code readability, naming conventions, and proper error/exception handling.",
          "Instruct the Gem to force friction: \"Do not give a pass easily. Critique the code constructively, highlight missing edge-case handling, and suggest refactored code blocks for any identified issues.\""
        ],
        "link": "https://gemini.google.com/app",
        "estimatedTime": "15 minutes",
        "verification": "You succeeded if your custom Gem replies with a structured, critical code review evaluating your snippet against security, performance, and maintainability—complete with refactored code suggestions. Verify it did not overstep its remit or complicate outputs with unrequired asks. If it did that, what was the problem, refine the Gem to simplify and limit its scope."
      },
      "id": "security-and-code-review-auditor"
    },
    {
      "title": "Using Apps Script in Google Docs as a brand styler and metadata generator",
      "tool": "gemini",
      "preferences": {
        "data": ["advanced"],
        "enabling-teams": ["advanced"],
        "product-analyst-delivery": ["advanced"],
        "product-analyst-product": ["advanced"],
        "product-analyst-ux": ["advanced"]
      },
      "content": {
        "paragraphs": [
          "Objective: Prompt Gemini to build a single Google Docs script that enforces official brand typography and heading colors while calculating and appending document metadata (word count, read time, and timestamp) in the footer."
        ],
        "steps": [
          "Scroll to the bottom of the page and select \"Open Gemini and start\".  A new tab will open and you'll be able to make a copy of a messily formatted document. with non-AND formatted text, including a title, mixed headings, and several paragraphs.",
          "Ask **Gemini/Gemini Enterprise** in a new chat [NOT Gemini within the document]: \"Write a Google Apps Script for Google Docs that performs two tasks: First, enforce brand styling across the document by setting Normal text to 11pt Roboto with 1.15 line spacing, Heading 1 to 18pt Poppins Bold in black, and further Headings to 14pt Poppins Bold in dark gray. Second, calculate the total word count and estimated reading time (assuming 200 words per minute), then append a footer displaying 'Word Count: [X] | Estimated Read Time: [Y] mins | Standardised: [Current Date]'. The script must also check if the document has a footer and if none is found, create a new footer. We should be able to use a custom menu to access these capabilities.\"",
          "Return to the Google Doc. Select **Extensions > Apps Script**. In the app script container (the light grey box) paste your script over the existing text (‘function myFunction() { }’). Then click the **floppy disk icon** to save the script (the script is usually called: code.gs).",
          "Exit the Apps Script window and navigate back to your Google doc. **Refresh** the doc. Shortly after refreshing, at the end of the menu options at the top of the sheet (next to the ‘help’ or ‘accessibility’ options), you should now see a custom menu called **Brand Tools**. Open the custom menu and run your style fixer (authorise the necessary permissions).",
          "Then return to your Gemini chat and prompt: \"Update the script to check for draft notes before formatting. The script should search the document for these common phrases: TO DO, INSERT, NEED TO ADD, and MISSING. If it finds any of these phrases: 1) Highlight them in yellow. 2) Show a pop-up window that tells the user how many draft notes were found and asks 'Do you want to continue formatting?' with Yes and No buttons. If the user clicks No, stop the script. If Yes, finish formatting.\"",
          "You will need to re-open **Extension > Apps Script** as this closed when we reloaded the document. Paste the new script over the old one and press the **save** icon.",
          "Return to your Google Doc. Refresh it. Add a few extra notes into the main body Google Doc text, like ‘TO DO’ or ‘INSERT’. Then run the updated script."
        ],
        "link": "https://docs.google.com/document/d/1Vn0t7HWmLoSsl1N062-SLVnUswmkjU_GNcoZRVfO_5s/copy",
        "estimatedTime": "20 minutes",
        "verification": "Confirm that the pop-up modal correctly identifies the placeholders, has coloured them yellow and given you a Choice (Yes/No) before making any formatting or metadata changes. You’ve successfully iterated your code to make improvements and flagged the presence of placeholder tags. Now you have practiced with a dummy doc, try out a new Apps Script on a doc of your own…!"
      },
      "id": "apps-script-brand-styler"
    },
    {
      "title": "a multi-step simulation with added AI features",
      "tool": "gemini",
      "preferences": {
        "product-developers-engineers": ["advanced"]
      },
      "content": {
        "paragraphs": [
          "Objective: You want to demo a slightly more complex tool, a wait-time simulator that reacts to multiple inputs."
        ],
        "steps": [
          "Go to gemini click Canvas on the drop down option from the prompt bar,  ask to build a wait-time simulation with several linked inputs (e.g., loan amount, interest rate, term) and a chart that updates live as inputs change, please do so in a programming language you are proficient in and can later review.",
          "Ask Gemini to add an AI-powered feature to the app",
          "Canvas supports \"Add Gemini features,\" like text or image generation baked into your prototype: e.g., a button that generates a plain-English summary of the simulation result.",
          "Feed the app edge-case inputs (zero, negative, huge numbers), capture what breaks via the console, and have Gemini refactor the logic to handle those cases gracefully.",
          "Finish by asking it to explain, in the chat, what changed and why."
        ],
        "link": "https://gemini.google.com/app",
        "estimatedTime": "20 minutes",
        "verification": "Verificiation: You have been able to demo a slightly more complex tool, a wait-time simulator that reacts to multiple inputs."
      },
      "id": "multi-step-simulation-with-added-ai-features"
    },
    {
      "title": "Natural Language Analytics with Databricks Samples",
      "tool": "genie",
      "preferences": {
        "data": ["beginner"]
      },
      "content": {
        "paragraphs": [
          "Objective: Query a governed dataset and uncover operational insights using natural language, without writing SQL.",
          "Scenario: You need to analyse order volume and value by priority using genie_training.exercises.orders to prepare a business summary for leadership."
        ],
        "steps": [
          "In the sidebar, click Genie Agents.",
          "Click New (top right). In the data picker:  \nexpand genie_training  \nexpand exercises  \ntick orders  \nclick Create  \nYou should now see the agent chat interface. Genie Code opens on the right and suggests table descriptions and example queries. Accept or ignore these for now.  \nBefore asking anything, go to Configure → Settings and confirm the Warehouse reads genie-training-wh.",
          "In the prompt bar, paste exactly — don't rephrase:  \n'Show me the total order count and the average total price, grouped by order priority, sorted by order priority.'  \nYou should now see five rows, one per priority level, with labels in the form 1-URGENT, 2-HIGH, and so on.",
          "Paste this follow-up exactly:  \n'For orders with priority 1-URGENT placed in 1995, show the order count by month, earliest month first.'  \nYou should now see twelve rows, one per month of 1995.",
          "Click Show code to inspect the generated filter and aggregation. Check two things: the date filter covers all of 1995, and the priority filter matches the 1-URGENT literal rather than a LIKE '%URGENT%' pattern.",
          "You should be able to see the results from your two queries in the same chat"
        ],
        "link": "https://accounts.cloud.databricks.com/login",
        "estimatedTime": "5 minutes",
        "verification": "You succeeded if Genie generates valid SQL against samples.tpch.orders, applies the date/priority filters correctly, and renders a monthly trend visual in under 5 minutes."
      },
      "id": "natural-language-analytics"
    },
    {
      "title": "Teaching Custom Business Logic & Definitions",
      "tool": "genie",
      "preferences": {
        "data": ["beginner"]
      },
      "content": {
        "paragraphs": [
          "Objective: Add business logic to a Genie Agent so non-technical users can query a concept that does not exist as a column.",
          "Scenario: Leadership tracks Airport Runs — trips ending at JFK (zip 11430) or LaGuardia (zip 11371). Nothing in the data says \"airport\"; the zip codes are just numbers. You need to teach Genie this definition so the team can ask for airport runs in plain English.",
          "Why this works as an exercise: Genie can infer that trip_distance means distance. It cannot possibly infer that 11430 means JFK. That gap is exactly what instructions are for — and you will see the difference by asking the same question before and after."
        ],
        "steps": [
          "In the sidebar, click Genie Agents.",
          "Click New (top right). In the data picker:  \n  \nexpand genie_training  \nexpand exercises  \ntick trips  \nclick Create  \nBefore asking anything, go to Configure → Settings and confirm Warehouse reads genie-training-wh.",
          "Before teaching Genie anything, paste this exactly:  \n  \nHow many airport runs were there?  \nExpect a poor answer — a guess, a clarifying question, or an error. Note what it does. This is your baseline.",
          "Click Configure, then open the Text tab. In the General instructions field, add:  \n  \n*Airport Run: a trip where dropoff_zip is 11430 (JFK) or 11371 (LaGuardia).*  \n*Long-Haul Trip: a trip where trip_distance is greater than 5 miles.*  \n*Fare per mile: for a single trip, fare_amount divided by trip_distance.*  \n*When asked for AVERAGE fare per mile, average the per-trip ratio -*  \n*AVG(fare_amount / trip_distance) - not total fare divided by total distance.*  \n*Why that last line is so specific: \"average fare per mile\" has two valid readings — the mean of each trip's ratio, or total fare divided by total distance. They give different numbers. Without pinning it down, two trainees get two answers and both think they're right.*  \n  \nSave your changes.  \n  \nNote: the whole General instructions block counts as one instruction against the agent's limit of 100. Example SQL queries and SQL functions each count as one too — so you have plenty of room here.",
          "Return to the chat and paste your baseline question again, exactly:  \n  \n*How many airport runs were there?*  \n  \nYou should now see a single number, and the code should filter dropoff_zip IN (11430, 11371). Compare against your Step 3 baseline — this contrast is the point of the exercise.",
          "Paste this exactly:  \n  \n*Count Airport Runs by pickup zip code, showing the average fare amount, highest count first.*",
          "Click Show code and verify the WHERE clause uses both zip codes, not just one.",
          "Paste this follow-up exactly:  \n  \n*For Long-Haul Trips that are also Airport Runs, show the average fare per mile.*  \n  \nThis tests whether Genie composed two of your definitions and did the division correctly. Click Show code and check two things: that both conditions are present (dropoff_zip IN (...) and trip_distance > 5), and that the average is AVG(fare_amount / trip_distance) rather than SUM(fare_amount) / SUM(trip_distance).  \n  \nNote your trip count. If it comes back as the same total you got in Step 5, Genie dropped the Long-Haul condition — it applied one definition, not two. Ask it again, naming both explicitly."
        ],
        "link": "https://accounts.cloud.databricks.com/login",
        "estimatedTime": "10 minutes",
        "verification": "You succeeded if Genie applies your custom mathematical instructions to the standard samples.nyctaxi.trips table and generates valid SQL without requiring you to explicitly write out the formula in your chat prompt."
      },
      "id": "business-logic-definitions"
    },
    {
      "title": "Cluster the sticky notes by theme",
      "tool": "miro-ai",
      "preferences": {
        "enabling-teams": ["beginner"],
        "product-analyst-delivery": ["beginner"],
        "product-analyst-product": ["beginner"],
        "product-analyst-ux": ["beginner"]
      },
      "content": {
        "paragraphs": [
          "Objective: Use the cluster tool to organise disorganised sticky notes by sentiment or keywords."
        ],
        "steps": [
          "Use the Miro Activity template (password: AIAugment) and find the frame 'Cluster the sticky notes by theme (Beginner)' if you have already completed a Miro activity return to the board.",
          "Select all of the sticky notes by dragging across them and click the Miro AI (sparkle icon) on the toolbar immediately above your selected sticky notes. (Do not use the toolbar on the left of the screen).",
          "Select ‘Cluster'",
          "Choose either keywords or sentiment"
        ],
        "link": "https://miro.com/app/board/uXjVHuZqtCo=/?share_link_id=527369488333",
        "estimatedTime": "5 minutes",
        "verification": "You completed this when you now have organised stickies by either sentiment or keywords."
      },
      "id": "cluster-sticky-notes"
    },
    {
      "title": "Custom Sidekick Lab (Bring Your Own Context)",
      "tool": "miro-ai",
      "preferences": {
        "enabling-teams": ["advanced"],
        "product-analyst-delivery": ["advanced"],
        "product-analyst-product": ["advanced"],
        "product-analyst-ux": ["advanced"]
      },
      "content": {
        "paragraphs": [
          "Objective: Build, test, and refine a bespoke Miro Sidekick configured with a custom system prompt and real project context to solve a specific consulting challenge."
        ],
        "steps": [
          "Use the Miro Activity template (password: AIAugment) and find the frame 'Bring your own context'. If you have already completed a Miro activity, return to the board.",
          "Open Miro’s Custom Sidekick Builder. Name your tool and write a custom system prompt using the 4-Part Framework (Role, Task, Constraints, Output Format).",
          "Paste a snippet of real project context into the Sidekick’s Knowledge Base section (e.g., an SOW excerpt, client governance rules, or brand/WCAG guidelines). (Please make sure you can use AI tools with this client, if not use internal work or ask Miro AI to create context for you)",
          "Select context and execute your custom Sidekick to generate structured output. Then Iterate & Refine"
        ],
        "link": "https://miro.com/app/board/uXjVHuZqtCo=/?share_link_id=527369488333",
        "estimatedTime": "30 minutes",
        "verification": "You’ve been successful if you have built a custom sidekick that has given you the outputs you have asked for."
      },
      "id": "custom-sidekick-lab"
    },
    {
      "title": "Pharmacy App Debrief",
      "tool": "miro-ai",
      "preferences": {
        "enabling-teams": ["intermediate"],
        "product-analyst-delivery": ["intermediate"],
        "product-analyst-product": ["intermediate"],
        "product-analyst-ux": ["intermediate"]
      },
      "content": {
        "paragraphs": [
          "Objective: Use Miro AI to generate reports, create impact VS effort grid, prioritise work based on user feedback and create an action list."
        ],
        "steps": [
          "Use the Miro Activity template (password: AIAugment) and find the frame 'Pharmacy App Debrief'. If you have already completed a Miro activity, return to the board.",
          "Locate the section titled “Understanding Sentiment”  and follow the instructions to produce a Sentiment Analysis report.",
          "Move on to the frame 'Impact vs Effort' and create an Impact vs Effort 2x2 matrix.",
          "Find the next section titled  'Top 3 Priority Improvements' to use the Impact vs Effort Matrix along the Interview transcript to identify the Top 3 areas to focus upon.",
          "Finally, on the section titled 'Action Items & Next Steps'  and use Miro AI to put an Action Plan together."
        ],
        "link": "https://miro.com/app/board/uXjVHuZqtCo=/?share_link_id=527369488333",
        "estimatedTime": "25 minutes",
        "verification": "You succeeded when you have a clear prioritised action list and assigned owners based on the initial 'workshop'."
      },
      "id": "pharmacy-app-debrief"
    },
    {
      "title": "The Agile Coach (Use a pre-made Sidekick)",
      "tool": "miro-ai",
      "preferences": {
        "product-analyst-delivery": ["intermediate"]
      },
      "content": {
        "paragraphs": [
          "Objective: Use pre-made Miro sidekick to extract system constraints, failure modes, and technical edge cases from the architecture spec to generate testable, INVEST-compliant Given-When-Then acceptance criteria that engineers can estimate for sprint planning."
        ],
        "steps": [
          "Use the Miro Activity Use the Miro Activity template (password: AIAugment) and find the frame 'Try out a sidekick'.  If you have already completed a Miro activity, return to the board.",
          "Find the section 'Agile Coaching' sidekick.",
          "Open the Miro AI (Purple sparkle icon), click the 'sidekicks library' (the icon that shows four boxes) and search 'Agile Coaching'",
          "Follow the instructions on the Miro Board"
        ],
        "link": "https://miro.com/app/board/uXjVHuZqtCo=/?share_link_id=527369488333",
        "estimatedTime": "15 minutes",
        "verification": "Your output includes explicit Given-When-Then criteria."
      },
      "id": "agile-coach"
    },
    {
      "title": "The Mind Mapper",
      "tool": "miro-ai",
      "preferences": {
        "enabling-teams": ["beginner"],
        "product-analyst-delivery": ["beginner"],
        "product-analyst-product": ["beginner"],
        "product-analyst-ux": ["beginner"]
      },
      "content": {
        "paragraphs": [
          "Objective: Use Miro's AI mind map generation to generate ideas, questions or topics."
        ],
        "steps": [
          "Use the Miro Activity template (password: AIAugment) and find the frame 'Mind Mapping (Beginner)' if you have already completed a Miro activity return to the board.",
          "Enter a topic of choice in the middle node",
          "Click on the middle node and click the Miro AI (sparkle icon) on the toolbar immediately above your selected node. (Do not use the toolbar on the left of the screen).",
          "'Expand mindmap'",
          "Select how you want AI to expand"
        ],
        "link": "https://miro.com/app/board/uXjVHuZqtCo=/?share_link_id=527369488333",
        "estimatedTime": "5 minutes",
        "verification": "You completed this when you created a mind map in minutes."
      },
      "id": "mind-mapper"
    },
    {
      "title": "The Product Leader (Use a pre-made Sidekick)",
      "tool": "miro-ai",
      "preferences": {
        "product-analyst-product": ["intermediate"]
      },
      "content": {
        "paragraphs": [
          "Objective: Use pre-made Miro sidekick to quickly link business metrics directly to give-when-then acceptance criteria."
        ],
        "steps": [
          "Use the Miro Activity template (password: AIAugment) and find the frame 'Try out a sidekick'.If you have already completed a Miro activity, return to the board.",
          "Find the section 'Product Leader sidekick'",
          "Open the Miro AI (Purple sparkle icon), click the 'sidekicks library' (the icon that shows four boxes) and search 'Product Leader'.",
          "Follow the instructions on the Miro Board"
        ],
        "link": "https://miro.com/app/board/uXjVHuZqtCo=/?share_link_id=527369488333",
        "estimatedTime": "15 minutes",
        "verification": "Your output clearly links business metrics directly to Given-When-Then acceptance criteria, prioritisation and risks outlined."
      },
      "id": "product-leader"
    },
    {
      "title": "The User Researcher (Use a pre-made Sidekick)",
      "tool": "miro-ai",
      "preferences": {
        "product-analyst-ux": ["intermediate"]
      },
      "content": {
        "paragraphs": [
          "Objective: Use pre-made Miro sidekick to synthesise qualitative user observations, micro-copy friction, and accessibility flags from the audit log into Given-When-Then user journey scenarios that specify precise UI interaction states and inclusive behaviors."
        ],
        "steps": [
          "Use the Miro Activity template (password: AIAugment) and find the frame 'Try out a sidekick'.If you have already completed a Miro activity, return to the board.",
          "Find the section 'UX Research' sidekick",
          "Open the Miro AI (Purple sparkle icon), click the 'sidekicks library' (the icon that shows four boxes) and search 'UX Research'",
          "Follow the instructions on the Miro Board"
        ],
        "link": "https://miro.com/app/board/uXjVHuZqtCo=/?share_link_id=527369488333",
        "estimatedTime": "15 minutes",
        "verification": "Your output clearly specifies interface states (e.g., loading spinners, disabled buttons) and accessible Given-When-Then behaviours based on real user observations."
      },
      "id": "user-researcher"
    },
    {
      "title": "The Workshop Planner (Utilitse a pre-made Sidekick)",
      "tool": "miro-ai",
      "preferences": {
        "enabling-teams": ["intermediate"]
      },
      "content": {
        "paragraphs": [
          "Objective: Use pre-made Miro sidekick to plan a workshop from initial ideas on stickies."
        ],
        "steps": [
          "Use the Miro Activity template (password: AIAugment) and find the frame 'Try out a sidekick'.If you have already completed a Miro activity, return to the board.",
          "Find the section 'Workshop planner' sidekick",
          "Open the Miro AI (Purple sparkle icon), click the 'sidekicks library' (the icon that shows four boxes) and search 'Workshop planner'",
          "Follow the instructions on the Miro Board"
        ],
        "link": "https://miro.com/app/board/uXjVHuZqtCo=/?share_link_id=527369488333",
        "estimatedTime": "15 minutes",
        "verification": "Your output turns initial ideas and context on stickies into a workshop plan."
      },
      "id": "workshop-planner"
    },
    {
      "title": "Workflow Automation (Try out a Flow)",
      "tool": "miro-ai",
      "preferences": {
        "enabling-teams": ["advanced"],
        "product-analyst-delivery": ["advanced"],
        "product-analyst-product": ["advanced"],
        "product-analyst-ux": ["advanced"]
      },
      "content": {
        "paragraphs": [
          "Objective: Convert an idea into a creative brief, a visual mock up, user stories, a diagram and slides."
        ],
        "steps": [
          "Use the Miro Activity template (password: AIAugment) and find the frame 'Create a Custom Flow’'. If you have already completed a Miro activity, return to the board.",
          "Read the instructions",
          "Follow the steps outlined in the frame",
          "Verify its output!"
        ],
        "link": "https://miro.com/app/board/uXjVHuZqtCo=/?share_link_id=527369488333",
        "estimatedTime": "20 minutes",
        "verification": "You’ve been successful when you have developed a creative brief, a visual mock up, user stories, a diagram and slides without much manual entry."
      },
      "id": "workflow-automation"
    },
    {
      "title": "Conversational Visualisations on Built-in Data",
      "tool": "sigma",
      "preferences": {
        "data": ["beginner"]
      },
      "content": {
        "paragraphs": [
          "Objective: Use Sigma AI within a workbook canvas to perform ad-hoc aggregations and render chart elements on Sigma’s native sample database.",
          "Scenario: You need to evaluate retail sales performance using Sigma's built-in Plugs Electronics sample connection without manually writing complex formula syntax or creating manual pivot tables."
        ],
        "steps": [
          "Open a new Sigma Workbook and select the native sample dataset connection by navigating to Connections on the left-hand bar on the Sigma homepage - Sigma Sample Database - Examples - PLUGS_ELECTRONICS_HANDS_ON_LAB_DATA.",
          "Click the AI Assistant icon in the top element toolbar.",
          "Enter the prompt: \"Calculate total profit margin percentage by store region and format it as a sorted bar chart.\"",
          "Refine the visualisation with a follow-up prompt: \"Add a cumulative revenue line for top 5 product categories in the West region.\" (Running this query once works smoothly, though repeated attempts in the same session may trigger errors).",
          "To publish all the created aggregations and charts into your workbook page layout, click the Share button on the top right of each graph to automatically generate the workbook containing all the data and visualisations."
        ],
        "link": "https://app.sigmacomputing.com/login",
        "estimatedTime": "10 minutes",
        "verification": "Confirm that both the sorted bar chart and cumulative revenue line elements appear directly on the workbook page layout with non-zero calculated values displayed correctly."
      },
      "id": "conversational-visualisations"
    },
    {
      "title": "Dynamic Executive Dashboard Narratives",
      "tool": "sigma",
      "preferences": {
        "data": ["beginner"]
      },
      "content": {
        "paragraphs": [
          "Objective: Build dynamic AI narrative elements in Sigma that automatically generate text summaries explaining performance anomalies whenever dashboard filters change.",
          "Scenario: Business stakeholders spend too much time reading complex dashboard charts. You will build a dynamic AI text card using standard sample data that updates its written executive summary in real time when filters are adjusted."
        ],
        "steps": [
          "Open your Plugs Electronics sample workbook in Sigma, add a Region Control Filter to the top of the canvas, and map it to your dataset column so it filters the canvas elements correctly.",
          "Add a Chat UI element to the dashboard header section.",
          "Connect the AI agent to the Plugs Electronics data source and configure it with the prompt instructions: \"You are a senior data analyst and expert on the PLUGS_ELECTRONICS_HANDS_ON_LAB_DATA data source. Your job is to analyse revenue and sales performance of products in that data source and turn findings into clear, actionable executive summaries. Prioritise the most impactful insights from a sales performance perspective. Always summarise findings in 3 bullet points or fewer, keep language simple and free of jargon and use a professional but friendly tone.\"",
          "Test the Agent chat with the following question: \"Analyse the total sales and margin performance for [Region Filter]. Generate a 3-bullet point executive summary highlighting top performance drivers.\"",
          "Change the Region Control Filter dropdown selection (e.g., switch from East to West) and prompt the agent to run."
        ],
        "link": "https://app.sigmacomputing.com/login",
        "estimatedTime": "10 minutes",
        "verification": "Confirm that the AI agent references the updated filter context and generates a fresh 3-bullet executive summary matching the newly selected region."
      },
      "id": "executive-dashboard-narratives"
    }
  ]
}
